// client/src/pages/ShopPOS/hooks/usePOSCart.ts
import { useState } from "react";
import { toast } from "@/hooks/use-toast";

interface CartItem {
  inventoryId: string;
  quantity: number;
  surchargePercentage?: number;
}

export function usePOSCart(
  fullInventory: any[], // ← PRIMER PARÁMETRO: Inventario completo
  getCurrencySymbol: () => string,
) {
  const [cart, setCart] = useState<CartItem[]>([]);

  // Agregar producto al carrito
  const addToCart = (inventoryId: string) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.inventoryId === inventoryId);
      if (existing) {
        const invItem = fullInventory.find((i: any) => i.id === inventoryId);
        if (invItem && existing.quantity >= invItem.stock) {
          toast({
            title: "Stock Insuficiente",
            description: "No hay más unidades disponibles.",
            variant: "destructive",
          });
          return prev;
        }
        return prev.map((item) =>
          item.inventoryId === inventoryId
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      }
      return [...prev, { inventoryId, quantity: 1, surchargePercentage: 0 }];
    });
  };

  // Eliminar producto del carrito
  const removeFromCart = (inventoryId: string) => {
    setCart((prev) => prev.filter((item) => item.inventoryId !== inventoryId));
  };

  // Actualizar cantidad
  const updateQuantity = (inventoryId: string, delta: number) => {
    setCart((prev) => {
      return prev.map((item) => {
        if (item.inventoryId === inventoryId) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return item;
          const invItem = fullInventory.find((i: any) => i.id === inventoryId);
          if (invItem && newQty > invItem.stock) return item;
          return { ...item, quantity: newQty };
        }
        return item;
      });
    });
  };

  // Actualizar porcentaje de recargo
  const updateSurcharge = (inventoryId: string, percentage: number) => {
    setCart((prev) =>
      prev.map((item) =>
        item.inventoryId === inventoryId
          ? {
              ...item,
              surchargePercentage: Math.min(100, Math.max(0, percentage)),
            }
          : item,
      ),
    );
  };

  // Vaciar carrito
  const clearCart = () => {
    if (cart.length === 0) return;
    setCart([]);
    toast({
      title: "Carrito vaciado",
      description: "Se han eliminado todos los productos del carrito",
      duration: 2000,
    });
  };

  // Calcular total del carrito - USANDO fullInventory
  const cartTotal = cart.reduce((sum, cartItem) => {
    const item = fullInventory.find((i: any) => i.id === cartItem.inventoryId);
    return sum + (item ? Number(item.price) * cartItem.quantity : 0);
  }, 0);

  // Calcular total con recargo - USANDO fullInventory
  const cartTotalWithSurcharge = cart.reduce((sum, cartItem) => {
    const item = fullInventory.find((i: any) => i.id === cartItem.inventoryId);
    if (!item) return sum;
    const subtotal = Number(item.price) * cartItem.quantity;
    const surchargePercentage = cartItem.surchargePercentage || 0;
    const surchargeAmount = subtotal * (surchargePercentage / 100);
    return sum + subtotal + surchargeAmount;
  }, 0);

  return {
    cart,
    setCart,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateSurcharge,
    clearCart,
    cartTotal,
    cartTotalWithSurcharge,
  };
}
